import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { solutionKeys } from "@/lib/solution-keys";

export interface ShareLinkRow {
  id: string;
  solutionId: string;
  versionId: string | null;
  token: string;
  createdBy: string;
  expiresAt: string | null;
  hideConcerns: boolean;
  hideComments: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface SharePublicPayload {
  share: { token: string; hideConcerns: boolean; hideComments: boolean };
  solution: { id: string; name: string; slug: string; description: string | null };
  version: {
    id: string;
    versionNumber: number;
    label: string | null;
    createdAt: string;
  };
  parsedData: unknown;
  mediaLinks: Array<{
    id: string;
    label: string;
    url: string;
    type: string;
    sortOrder: number;
  }>;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    let message = `${res.status}`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export function useShares(slug: string) {
  return useQuery({
    queryKey: solutionKeys.shares(slug),
    enabled: Boolean(slug),
    queryFn: async (): Promise<ShareLinkRow[]> => {
      const json = await api<{ data: ShareLinkRow[] }>(`/api/solutions/${slug}/shares`);
      return json.data;
    },
  });
}

export function useCreateShare(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      versionId?: string | null;
      hideConcerns?: boolean;
      hideComments?: boolean;
      expiresAt?: string | null;
    }) => {
      const json = await api<{ data: ShareLinkRow }>(`/api/solutions/${slug}/shares`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.shares(slug) });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to create share link");
    },
  });
}

export function useUpdateShare(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      shareId: string;
      isActive?: boolean;
      hideConcerns?: boolean;
      hideComments?: boolean;
    }) => {
      const { shareId, ...rest } = input;
      const json = await api<{ data: ShareLinkRow }>(`/api/shares/${shareId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(rest),
      });
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.shares(slug) });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to update share link");
    },
  });
}

export function useSharePublic(token: string) {
  return useQuery({
    queryKey: solutionKeys.share(token),
    enabled: Boolean(token),
    queryFn: async (): Promise<SharePublicPayload> => {
      const json = await api<{ data: SharePublicPayload }>(`/api/share/${token}`);
      return json.data;
    },
  });
}
